<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { teamsApi } from '@/services/api';
import type { Team, PaymentType } from '@/types/models';
import AppBadge from '@/components/ui/AppBadge.vue';
import AppModal from '@/components/ui/AppModal.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NInput, NButton, NCard, NTag } from 'naive-ui';
import {
  Trophy,
  Plus,
  Users2,
  Calendar,
  Search,
  CreditCard,
  Video,
  Dumbbell,
  ShieldCheck,
  Building2,
  Users,
  RefreshCw
} from 'lucide-vue-next';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const router = useRouter();
const { activeTenant, managedTeams, currentUser } = useTenant();
const { notifySuccess, notifyError } = useToastNotification();

const isTeamAdmin = computed(() =>
  currentUser.value.role === 'ADMIN' ||
  currentUser.value.role === 'TEAM_ADMIN' ||
  currentUser.value.role === 'ORG_ADMIN'
);

const searchQuery = ref('');
const isAddModalOpen = ref(false);
const isLoading = ref(false);

const loadTeams = async () => {
  isLoading.value = true;
  try {
    const teams = await teamsApi.getTeams(activeTenant.value.id);
    if (teams && teams.length > 0) {
      const otherTeams = managedTeams.value.filter(t => t.tenantId !== activeTenant.value.id);
      managedTeams.value = [...otherTeams, ...teams];
    }
  } catch (err) {
    console.warn('[TeamsView] Could not load teams from API:', err);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadTeams();
});

watch(() => activeTenant.value.id, () => {
  loadTeams();
});

// Filtered teams by tenant and search query
const filteredTeams = computed(() => {
  return managedTeams.value
    .filter(t => t.tenantId === activeTenant.value.id)
    .filter(t => {
      const q = searchQuery.value.toLowerCase();
      return (
        t.name.toLowerCase().includes(q) ||
        t.division.toLowerCase().includes(q) ||
        t.headCoach.toLowerCase().includes(q)
      );
    });
});

// Form state for creating a new team
const form = ref({
  name: '',
  season: '2025年 公式戦',
  division: '都市対抗・社会人野球',
  headCoach: '',
  color: '#0284c7',
  paymentType: 'TEAM' as PaymentType,
});

const isSubmitting = ref(false);

const handleCreateTeam = async () => {
  if (!form.value.name || !form.value.headCoach) return;

  isSubmitting.value = true;
  try {
    const created = await teamsApi.createTeam({
      tenantId: activeTenant.value.id,
      name: form.value.name,
      season: form.value.season,
      division: form.value.division,
      headCoach: form.value.headCoach,
      color: form.value.color,
      paymentType: form.value.paymentType,
    });

    if (created && !managedTeams.value.some(t => t.id === created.id)) {
      managedTeams.value.push(created);
    }
    isAddModalOpen.value = false;
    notifySuccess(t('teams.saveTeam'), t('teams.teamCreatedSuccess', { name: created.name }));

    // Reset form
    form.value = {
      name: '',
      season: '2025 Spring Showcase',
      division: 'High School Elite',
      headCoach: '',
      color: '#0284c7',
      paymentType: 'TEAM',
    };
  } catch (err: any) {
    notifyError(t('teams.createTeamFailed'), err.message);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div>
        <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
          {{ t('teams.title') }}
        </h1>
        <p class="mt-1 text-xs sm:text-sm text-slate-400">
          {{ t('teams.subtitle', { name: activeTenant.name }) }}
        </p>
      </div>

      <div class="flex items-center gap-2.5">
        <NButton
          secondary
          @click="loadTeams"
          :loading="isLoading"
          class="font-semibold text-xs"
          :title="t('teams.syncWithApi')"
        >
          <template #icon>
            <RefreshCw class="w-4 h-4 text-sky-400" />
          </template>
          {{ t('common.sync') }}
        </NButton>

        <NButton
          v-if="isTeamAdmin"
          secondary
          @click="router.push('/dashboard/billing')"
          class="font-semibold text-xs"
        >
          <template #icon>
            <CreditCard class="w-4 h-4 text-sky-400" />
          </template>
          {{ t('teams.stripeBilling') }}
        </NButton>

        <NButton
          type="primary"
          @click="isAddModalOpen = true"
          class="font-bold text-xs shadow-lg shadow-sky-500/20"
        >
          <template #icon>
            <Plus class="w-4 h-4" />
          </template>
          {{ t('teams.addNewTeam') }}
        </NButton>
      </div>
    </div>

    <!-- Search / Filter Bar -->
    <div class="max-w-md">
      <NInput
        v-model:value="searchQuery"
        :placeholder="t('teams.searchPlaceholder')"
        clearable
        size="medium"
      >
        <template #prefix>
          <Search class="w-4 h-4 text-slate-400" />
        </template>
      </NInput>
    </div>

    <!-- Team Cards Grid -->
    <div v-if="filteredTeams.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <NCard
        v-for="team in filteredTeams"
        :key="team.id"
        hoverable
        class="bg-slate-900 border-slate-800 rounded-2xl hover:border-slate-700 transition flex flex-col justify-between group shadow-xl"
      >
        <div>
          <!-- Header with color accent -->
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-md"
                :style="{ backgroundColor: team.color || '#0284c7' }"
              >
                <Trophy class="w-5 h-5" />
              </div>
              <div>
                <h3 class="font-extrabold text-slate-100 text-base group-hover:text-sky-400 transition">
                  {{ team.name }}
                </h3>
                <p class="text-xs text-slate-400">{{ team.season }}</p>
              </div>
            </div>
            <AppBadge variant="primary">{{ team.division }}</AppBadge>
          </div>

          <!-- Payment Model & Subscriptions Bar -->
          <div class="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <span class="text-[10px] uppercase font-bold text-slate-500">{{ t('teams.billingLabel') }}</span>
              <span
                :class="[
                  'text-[10px] font-bold px-2 py-0.5 rounded-md border flex items-center gap-1',
                  team.paymentType === 'TEAM'
                    ? 'bg-sky-500/15 text-sky-300 border-sky-500/30'
                    : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                ]"
              >
                <component :is="team.paymentType === 'TEAM' ? Building2 : Users" class="w-3 h-3" />
                {{ team.paymentType === 'TEAM' ? t('teams.teamMaster') : t('teams.splitDues') }}
              </span>
            </div>

            <!-- Active Subscribed Services Icons -->
            <div class="flex items-center gap-1.5">
              <span
                v-if="team.subscriptions?.some(s => s.serviceId === 'VIDEO_ANALYTICS' && s.status === 'ACTIVE')"
                class="w-6 h-6 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center"
                :title="t('teams.videoAnalyticsSubscribed')"
              >
                <Video class="w-3.5 h-3.5" />
              </span>
              <span
                v-if="team.subscriptions?.some(s => s.serviceId === 'SKILL_TRAINING' && s.status === 'ACTIVE')"
                class="w-6 h-6 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center"
                :title="t('teams.skillTrainingSubscribed')"
              >
                <Dumbbell class="w-3.5 h-3.5" />
              </span>
            </div>
          </div>

          <!-- Stats / Record -->
          <div class="mt-4 grid grid-cols-2 gap-3 py-3 px-4 bg-slate-950/60 rounded-xl border border-slate-800/80">
            <div>
              <span class="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block">{{ t('teams.record') }}</span>
              <span class="font-mono font-bold text-sm text-emerald-400">{{ team.wins }} - {{ team.losses }}</span>
            </div>
            <div>
              <span class="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block">{{ t('teams.rosterSize') }}</span>
              <span class="font-semibold text-sm text-slate-200">{{ team.playerCount || 16 }} {{ t('teams.players') }}</span>
            </div>
          </div>

          <div class="mt-3 text-xs text-slate-400">
            <span class="text-slate-500">{{ t('teams.headCoach') }} </span>
            <span class="text-slate-200 font-medium">{{ team.headCoach }}</span>
          </div>
        </div>

        <!-- Action Links -->
        <div class="mt-6 pt-4 border-t border-slate-800/80 flex items-center justify-between gap-2">
          <router-link
            :to="`/dashboard/teams/${team.id}/roster`"
            class="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs rounded-xl transition"
          >
            <Users2 class="w-3.5 h-3.5 text-sky-400" />
            <span>{{ t('teams.viewRoster') }}</span>
          </router-link>

          <router-link
            to="/dashboard/events"
            class="px-3 py-2 bg-slate-800/50 hover:bg-slate-800 text-slate-400 hover:text-slate-200 text-xs rounded-xl transition"
            :title="t('teams.schedule')"
          >
            <Calendar class="w-3.5 h-3.5" />
          </router-link>
        </div>
      </NCard>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-16 bg-slate-900/50 border border-slate-800/60 rounded-2xl p-8">
      <div class="w-12 h-12 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto text-slate-400 mb-3">
        <Trophy class="w-6 h-6" />
      </div>
      <h3 class="text-base font-bold text-slate-200">{{ t('teams.noTeams') }}</h3>
      <p class="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
        {{ t('teams.noTeamsDesc') }}
      </p>
      <NButton
        type="primary"
        @click="isAddModalOpen = true"
        class="mt-4 font-bold text-xs"
      >
        {{ t('teams.createFirstTeam') }}
      </NButton>
    </div>

    <!-- Modal: Add New Team -->
    <AppModal
      v-model="isAddModalOpen"
      :title="t('teams.createModalTitle')"
      :subtitle="t('teams.createModalSubtitle', { name: activeTenant.name })"
    >
      <form @submit.prevent="handleCreateTeam" class="space-y-4">
        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('teams.teamName') }}</label>
          <NInput
            v-model:value="form.name"
            placeholder="例: 東京ガス硬式野球部 Aチーム"
            required
          />
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('teams.season') }}</label>
            <NInput
              v-model:value="form.season"
              placeholder="例: 2025年 公式戦"
              required
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('teams.division') }}</label>
            <NInput
              v-model:value="form.division"
              placeholder="例: 都市対抗・社会人野球 (JABA)"
              required
            />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('teams.headCoach') }}</label>
            <NInput
              v-model:value="form.headCoach"
              placeholder="例: 工藤 裕樹 監督"
              required
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('teams.paymentModel') }}</label>
            <select
              v-model="form.paymentType"
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-sky-500"
            >
              <option value="TEAM">{{ t('teams.teamMasterPayment') }}</option>
              <option value="INDIVIDUAL">{{ t('teams.individualPlayerDues') }}</option>
            </select>
          </div>
        </div>

        <div class="pt-4 flex justify-end gap-3 border-t border-slate-800">
          <NButton
            quaternary
            @click="isAddModalOpen = false"
          >
            {{ t('teams.cancel') }}
          </NButton>
          <NButton
            type="primary"
            attr-type="submit"
            :loading="isSubmitting"
            class="font-bold text-xs"
          >
            {{ t('teams.saveTeam') }}
          </NButton>
        </div>
      </form>
    </AppModal>
  </div>
</template>
